import { getIsOnline } from "@client/hooks/useNetworkStatus";
import { cn } from "@client/utils/cn";
import { PlayIcon } from "lucide-react";
import { type ReactNode, useEffect, useState } from "react";

/**
 * One poster URL, or several to try in order — the first that loads wins, and
 * only when all of them fail does the placeholder show.
 *
 * The ordered form exists for the CDN thumbnails (see `videoPosterSources`): the
 * fast edge-served URL first, the always-correct proxy behind it. It is a list
 * rather than a second `fallbackPosterUrl` prop so the components between a page
 * and this one — MeetingThumbnailCard, QueueRow, the playlist rows — keep passing
 * a single poster prop and never have to know a fallback exists.
 */
export type VideoPosterSource = string | null | undefined;

// Shared poster surface for videos and clips across web and the iPhone app.
// Renders the poster image when available; otherwise (no poster, or every source
// fails to load — e.g. an expired/404 signed URL) falls back to a gradient
// placeholder with a play glyph. The gradient is the canonical "no poster"
// look used everywhere a video thumbnail can appear.
export function VideoPoster({
  posterUrl,
  className,
  iconClassName,
  fetchPriority,
  children,
}: {
  posterUrl?: VideoPosterSource | readonly VideoPosterSource[];
  className?: string;
  iconClassName?: string;
  // Pass "low" for thumbnail strips that render many posters at once next to
  // higher-value traffic (the funder workspace's meeting rail loads its whole
  // strip right as the selected video starts streaming).
  fetchPriority?: "low";
  // Optional overlays rendered on top of the poster (e.g. a progress stripe).
  children?: ReactNode;
}) {
  // Track the specific URLs that failed rather than a sticky boolean, so when
  // posterUrl changes (e.g. a failed online URL is replaced by a freshly
  // cached local src) the new source is attempted instead of staying stuck on
  // the placeholder. Also what makes the ordered form work: a failed CDN URL
  // drops out and the next candidate — the proxy — is rendered.
  const [failedUrls, setFailedUrls] = useState<readonly string[]>([]);
  const candidates = (
    Array.isArray(posterUrl) ? posterUrl : [posterUrl as VideoPosterSource]
  ).filter((url): url is string => url != null && url !== "");
  const src = candidates.find((url) => !failedUrls.includes(url)) ?? null;

  // A failed URL would otherwise latch for the component's whole life: poster
  // URLs are stable within a session, so "retry when the URL changes" never
  // recovers from a transient failure (network blip, app suspended mid-load).
  // Re-attempt the load when connectivity returns or the app comes back to
  // the foreground; a genuinely missing poster just fails back to the
  // placeholder.
  useEffect(() => {
    if (failedUrls.length === 0) {
      return;
    }

    function retry() {
      if (getIsOnline()) {
        setFailedUrls([]);
      }
    }

    function handleVisibilityChange() {
      if (document.visibilityState === "visible") {
        retry();
      }
    }

    window.addEventListener("online", retry);
    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      window.removeEventListener("online", retry);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [failedUrls]);

  return (
    <div
      className={cn(
        "relative flex items-center justify-center overflow-hidden bg-linear-to-br from-sky-100 to-slate-50 dark:from-sky-950 dark:to-slate-900",
        className,
      )}
    >
      {src != null ? (
        <img
          // Keyed by src so a fallback swap remounts the element. Without it
          // React only patches the attribute, and a browser that has already
          // errored on this <img> can skip loading the replacement.
          key={src}
          src={src}
          alt=""
          loading="lazy"
          fetchPriority={fetchPriority}
          // CORS mode to match every other poster <img> (the player's
          // crossOrigin="anonymous"), so a thumbnail and the mounted player
          // showing the same video share one poster fetch instead of
          // downloading the bytes twice. Safe everywhere this renders: the
          // web app's poster proxy is same-origin, the bundled native app
          // gets its origin echoed in Access-Control-Allow-Origin (app.ts),
          // and iPad local files resolve to the WebView's own
          // capacitor://localhost origin.
          crossOrigin="anonymous"
          className="size-full object-cover"
          onError={() =>
            setFailedUrls((failed) =>
              failed.includes(src) ? failed : [...failed, src],
            )
          }
        />
      ) : (
        <PlayIcon className={cn("size-5 text-text-secondary", iconClassName)} />
      )}

      {children}
    </div>
  );
}
